import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { dailyStandupAPI, spaceAPI, listAPI, taskAPI, workspaceAPI } from '../lib/api';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    cn,
    formatDate,
    getInitials,
    priorityConfig,
    statusColors,
} from '../lib/utils';
import {
    CheckCircle2,
    Clock,
    Calendar,
    Loader2,
    AlertTriangle,
    ClipboardList,
    Send,
    ChevronDown,
    ChevronRight,
    Inbox,
    X,
} from 'lucide-react';
import { toast } from 'sonner';

function getWeeksInMonth(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay  = new Date(year, month, 0);
    const fmt = (d) => d.toISOString().split('T')[0];
    const weeks = [];
    let cur = new Date(firstDay);
    const dow = cur.getDay();
    cur.setDate(cur.getDate() - (dow === 0 ? 6 : dow - 1));
    let idx = 1;
    while (cur <= lastDay) {
        const end = new Date(cur);
        end.setDate(end.getDate() + 6);
        weeks.push({
            label: `Minggu ${idx}`,
            from: fmt(cur < firstDay ? firstDay : cur),
            to:   fmt(end > lastDay  ? lastDay  : end),
        });
        cur.setDate(cur.getDate() + 7);
        idx++;
    }
    return weeks;
}

function getMonthRange(year, month) {
    const mm   = String(month).padStart(2, '0');
    const last = new Date(year, month, 0).getDate();
    return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];

export default function DailyStandupPage() {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [spaces, setSpaces] = useState([]);
    const [lists, setLists] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState('all');
    const [selectedList, setSelectedList] = useState('all');
    const [expandedMembers, setExpandedMembers] = useState({});
    const [sendingReminders, setSendingReminders] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [detailTask, setDetailTask] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);

    const todayWIB = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }))
        .toISOString().split('T')[0];

    const todayLocal = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
    const [viewMode, setViewMode] = useState('harian');
    const [filterYear, setFilterYear] = useState(todayLocal.getFullYear());
    const [filterMonth, setFilterMonth] = useState(todayLocal.getMonth() + 1);
    const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

    const fetchData = useCallback(async () => {
        if (!currentWorkspace?.id || !currentWorkspace.daily_standup_enabled) return;
        setLoading(true);
        try {
            const params = {};
            if (selectedSpace !== 'all') params.space_id = selectedSpace;
            if (selectedList !== 'all') params.list_id = selectedList;

            if (viewMode === 'harian') {
                if (selectedDate) params.date = selectedDate;
            } else if (viewMode === 'mingguan') {
                const weeks = getWeeksInMonth(filterYear, filterMonth);
                const week = weeks[selectedWeekIndex] ?? weeks[0];
                if (week) { params.date_from = week.from; params.date_to = week.to; }
            } else if (viewMode === 'bulanan') {
                const { from, to } = getMonthRange(filterYear, filterMonth);
                params.date_from = from;
                params.date_to   = to;
            }

            const [standupRes, spacesRes] = await Promise.all([
                dailyStandupAPI.getAll(currentWorkspace.id, params),
                spaceAPI.getByWorkspace(currentWorkspace.id),
            ]);

            setData(standupRes.data);
            setSpaces(spacesRes.data || []);

            // Auto-expand all members on first load
            const newExpanded = {};
            (standupRes.data?.members || []).forEach((m) => {
                newExpanded[m.user.id] = true;
            });
            setExpandedMembers(newExpanded);
        } catch (err) {
            console.error('Error fetching daily standup:', err);
            toast.error('Gagal memuat data Daily Standup');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id, currentWorkspace?.daily_standup_enabled, selectedSpace, selectedList, selectedDate, viewMode, filterYear, filterMonth, selectedWeekIndex]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch workspace members untuk TaskDetailFullModal
    useEffect(() => {
        if (!currentWorkspace?.id) return;
        workspaceAPI.getMembers(currentWorkspace.id)
            .then(res => setWorkspaceMembers(res.data || []))
            .catch(() => {});
    }, [currentWorkspace?.id]);

    // Reset week index saat bulan/tahun berubah
    useEffect(() => { setSelectedWeekIndex(0); }, [filterMonth, filterYear]);

    // Fetch lists when space changes
    useEffect(() => {
        if (selectedSpace !== 'all' && currentWorkspace?.id) {
            listAPI.getBySpace(selectedSpace).then((res) => {
                setLists(res.data || []);
            }).catch(() => setLists([]));
        } else {
            setLists([]);
        }
    }, [selectedSpace, currentWorkspace?.id]);

    const handleViewDetail = async (task) => {
        setDetailLoading(true);
        try {
            const res = await taskAPI.get(task.id);
            setDetailTask(res.data);
        } catch {
            setDetailTask(task);
        } finally {
            setDetailLoading(false);
        }
    };

    const toggleMember = (userId) => {
        setExpandedMembers((prev) => ({ ...prev, [userId]: !prev[userId] }));
    };

    const handleSendReminders = async () => {
        if (!currentWorkspace?.id) return;
        setSendingReminders(true);
        try {
            const res = await dailyStandupAPI.sendReminders(currentWorkspace.id);
            toast.success(`Notifikasi terkirim ke ${res.data.sent_count} member`);
        } catch (err) {
            console.error('Error sending reminders:', err);
            toast.error('Gagal mengirim pengingat');
        } finally {
            setSendingReminders(false);
        }
    };

    const unplannedCount = (data?.members || []).filter((m) => !m.has_plan).length;
    const totalMembers = data?.members?.length || 0;

    const displayDate = (() => {
        if (viewMode === 'mingguan' && data?.date_to) {
            const w = getWeeksInMonth(filterYear, filterMonth)[selectedWeekIndex];
            return w ? `${w.label} (${w.from} — ${w.to})` : (data.date || todayWIB);
        }
        if (viewMode === 'bulanan') {
            return `${MONTH_NAMES[filterMonth - 1]} ${filterYear}`;
        }
        return data?.date ? formatDate(data.date + 'T00:00:00') : formatDate(todayWIB + 'T00:00:00');
    })();

    const hasNoActivity =
        !data?.members?.length ||
        data.members.every(
            (m) =>
                m.done_yesterday.length === 0 &&
                m.in_progress.length === 0 &&
                m.planned_today.length === 0
        );

    const renderTaskItem = (task, { showDoneLabel = false } = {}) => {
        const priority = priorityConfig[task.priority] || priorityConfig.none;
        const statusColor = statusColors[task.status] || '#9FB0AC';

        let doneLabelEl = null;
        if (showDoneLabel && task.created_at) {
            const lastEntry = [...(task.status_history || [])].reverse().find(e => e.changed_at);
            if (lastEntry) {
                const diffDays = Math.max(0, Math.floor(
                    (new Date(lastEntry.changed_at) - new Date(task.created_at)) / 86400000
                ));
                doneLabelEl = (
                    <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                        change to {lastEntry.status || task.status} in {diffDays} day{diffDays !== 1 ? 's' : ''}
                    </span>
                );
            }
        }

        return (
            <div
                key={task.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer group"
                onClick={() => handleViewDetail(task)}
            >
                {/* Status dot */}
                <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: statusColor }}
                />
                {/* Task code + title */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {task.task_code && (
                            <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                                {task.task_code}
                            </span>
                        )}
                        <span className="text-sm font-medium truncate text-gray-700 dark:text-gray-200">
                            {task.title}
                        </span>
                    </div>
                    {task.parent_task_title && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">
                            ↳ {task.parent_task_code ? `${task.parent_task_code} · ` : ''}{task.parent_task_title}
                        </div>
                    )}
                </div>
                {/* Priority badge */}
                <span
                    className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                        backgroundColor: `${priority.color}20`,
                        color: priority.color,
                    }}
                >
                    {priority.label}
                </span>
                {/* Done label */}
                {doneLabelEl}
                {/* Due date */}
                {task.due_date && (
                    <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(task.due_date)}
                    </span>
                )}
            </div>
        );
    };

    const renderCategorySection = (title, Icon, tasks, color, extraProps = {}) => {
        return (
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color }}>
                        {title}
                    </span>
                    <span className="text-xs text-gray-400">({tasks.length})</span>
                </div>
                <div className="space-y-1">
                    {tasks.length === 0 ? (
                        <div className="text-xs text-gray-400 italic py-2 px-3">
                            Tidak ada task
                        </div>
                    ) : (
                        tasks.map(task => renderTaskItem(task, extraProps))
                    )}
                </div>
            </div>
        );
    };

    const renderMemberCard = (member) => {
        const isExpanded = expandedMembers[member.user.id];
        const initials = getInitials(member.user.name || member.user.email || '?');

        return (
            <Card key={member.user.id} className="overflow-hidden">
                <CardHeader
                    className="cursor-pointer pb-3"
                    onClick={() => toggleMember(member.user.id)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-brand-accent flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                {initials}
                            </div>
                            <div>
                                <CardTitle className="text-sm font-medium">
                                    {member.user.name || member.user.email}
                                </CardTitle>
                                <p className="text-xs text-gray-400">{member.user.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {!member.has_plan && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 font-medium">
                                    Belum ada rencana
                                </span>
                            )}
                            {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                        </div>
                    </div>
                </CardHeader>
                {isExpanded && (
                    <CardContent className="pt-0">
                        <div className="grid grid-cols-2 gap-4">
                            {renderCategorySection(
                                viewMode === 'harian' ? 'Selesai Kemarin' : 'Selesai',
                                CheckCircle2,
                                member.done_yesterday,
                                '#7ED957',
                                { showDoneLabel: true }
                            )}
                            {renderCategorySection(
                                viewMode === 'harian' ? 'Rencana Hari Ini' : 'Rencana',
                                Calendar,
                                member.planned_today,
                                '#EF4444'
                            )}
                        </div>
                    </CardContent>
                )}
            </Card>
        );
    };

    if (!currentWorkspace) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-gray-400">Pilih workspace terlebih dahulu</p>
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace.daily_standup_enabled) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
                    <Inbox className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">
                        Daily Standup dinonaktifkan untuk workspace ini
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                        Owner atau admin dapat mengaktifkannya di Settings → Workspace.
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <>
        <Layout>
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <ClipboardList className="w-6 h-6 text-brand-accent" />
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                            Daily Standup
                        </h1>
                        <p className="text-sm text-gray-400">
                            {data?.workspace_name} — {displayDate}
                        </p>
                    </div>
                </div>
                </div>

                {/* Mode tabs */}
                <div className="inline-flex items-center gap-1 mb-3 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
                    {[['harian','Harian'],['mingguan','Mingguan'],['bulanan','Bulanan']].map(([m, label]) => (
                        <button
                            key={m}
                            onClick={() => setViewMode(m)}
                            className={cn(
                                'px-4 py-1.5 text-sm rounded-md font-medium transition-colors',
                                viewMode === m
                                    ? 'bg-white dark:bg-gray-900 text-brand-accent shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            )}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    {/* Date filter — hanya untuk mode harian */}
                    {viewMode === 'harian' && (
                        <div className="relative flex items-center">
                            <Calendar className="absolute left-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="date"
                                value={selectedDate}
                                max={todayWIB}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="pl-8 pr-8 py-1.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            {selectedDate && (
                                <button
                                    onClick={() => setSelectedDate('')}
                                    className="absolute right-2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Bulan + tahun + minggu — untuk mingguan/bulanan */}
                    {(viewMode === 'mingguan' || viewMode === 'bulanan') && (
                        <>
                            <Select value={String(filterMonth)} onValueChange={(v) => setFilterMonth(Number(v))}>
                                <SelectTrigger className="w-[110px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MONTH_NAMES.map((name, i) => (
                                        <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
                                <SelectTrigger className="w-[90px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[filterYear - 1, filterYear, filterYear + 1].map((y) => (
                                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {viewMode === 'mingguan' && (() => {
                                const weeks = getWeeksInMonth(filterYear, filterMonth);
                                return (
                                    <Select value={String(selectedWeekIndex)} onValueChange={(v) => setSelectedWeekIndex(Number(v))}>
                                        <SelectTrigger className="w-[220px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {weeks.map((w, i) => (
                                                <SelectItem key={i} value={String(i)}>
                                                    {w.label} ({w.from} – {w.to})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                );
                            })()}
                        </>
                    )}

                    <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Semua Space" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Space</SelectItem>
                            {spaces.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                    {s.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {selectedSpace !== 'all' && lists.length > 0 && (
                        <Select value={selectedList} onValueChange={setSelectedList}>
                            <SelectTrigger className="w-[200px]">
                                <SelectValue placeholder="Semua List" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua List</SelectItem>
                                {lists.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>
                                        {l.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchData}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                    </Button>
                </div>

                {/* Banner: Unplanned members — hanya untuk mode harian */}
                {viewMode === 'harian' && !loading && data && totalMembers > 0 && !hasNoActivity && (
                    <div className={cn(
                        "flex items-center justify-between p-3 rounded-lg mb-4",
                        unplannedCount > 0
                            ? "bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800"
                            : "bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800"
                    )}>
                        <div className="flex items-center gap-2">
                            {unplannedCount > 0 ? (
                                <>
                                    <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                    <span className="text-sm text-yellow-700 dark:text-yellow-300">
                                        <strong>{unplannedCount}</strong> dari {totalMembers} member belum memiliki rencana task hari ini
                                    </span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm text-green-700 dark:text-green-300">
                                        Semua member sudah memiliki rencana task hari ini
                                    </span>
                                </>
                            )}
                        </div>
                        {unplannedCount > 0 && (
                            <Button
                                size="sm"
                                onClick={handleSendReminders}
                                disabled={sendingReminders}
                            >
                                {sendingReminders ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                ) : (
                                    <Send className="w-4 h-4 mr-1" />
                                )}
                                Kirim Pengingat
                            </Button>
                        )}
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-accent" />
                    </div>
                ) : !hasNoActivity ? (
                    <div className="space-y-3">
                        {data.members.map(renderMemberCard)}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Inbox className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-gray-500 font-medium">
                            Daily Standup tidak berjalan pada {displayDate}
                        </p>
                    </div>
                )}
            </div>
        </Layout>

        {detailLoading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
                <Loader2 className="w-8 h-8 animate-spin text-brand-accent" />
            </div>
        )}
        {detailTask && (
            <TaskDetailFullModal
                isOpen={!!detailTask}
                onClose={() => setDetailTask(null)}
                task={detailTask}
                listId={detailTask.list_id}
                statuses={[]}
                members={workspaceMembers}
                readOnly={false}
            />
        )}
        </>
    );
}